/**
 * 内容预处理器
 * 负责：反转义 JSON 字符串、提取 SVG 片段、拆分内容为可渲染段落
 */

export interface ContentSegment {
  readonly type: 'markdown' | 'svg';
  readonly content: string;
}

/**
 * 检测字符串是否为 JSON 转义格式
 * 特征：包含字面量 \n 或 \" 转义序列
 */
const isJsonEscaped = (text: string): boolean => {
  // 检测字面量 \" 或 \n（非真实换行）
  return /\\n|\\"/g.test(text);
};

/**
 * 反转义 JSON 字符串
 * 处理：\" → "、\n → 换行、\t → tab、\\ → \
 * 同时去除首尾可能存在的包裹双引号
 */
export const unescapeContent = (text: string): string => {
  let result = text;

  // 去除首尾包裹的双引号（JSON 字符串值的外层引号）
  if (result.startsWith('"') && result.endsWith('"')) {
    result = result.slice(1, -1);
  }

  if (!isJsonEscaped(result)) {
    return result;
  }

  // 按顺序替换转义序列（\\ 必须最先处理占位，最后还原）
  const BACKSLASH_PLACEHOLDER = '\u0000BACKSLASH\u0000';
  result = result.replace(/\\\\/g, BACKSLASH_PLACEHOLDER);
  result = result.replace(/\\n/g, '\n');
  result = result.replace(/\\t/g, '\t');
  result = result.replace(/\\"/g, '"');
  result = result.replace(/\\'/g, "'");
  result = result.replace(new RegExp(BACKSLASH_PLACEHOLDER.replace(/\u0000/g, '\\u0000'), 'g'), '\\');
  // 简化占位符还原
  result = result.split(BACKSLASH_PLACEHOLDER).join('\\');

  return result;
};

/**
 * 检测内容是否为 SVG
 */
const isSvgContent = (text: string): boolean => {
  const trimmed = text.trim();
  return trimmed.startsWith('<svg') && trimmed.includes('</svg>');
};

/**
 * 匹配裸 SVG 标签和 Markdown svg 代码块的正则
 *
 * 分两种情况捕获：
 * 1. Markdown 代码块：```svg\n...\n``` 或 ```xml\n<svg...\n```
 * 2. 裸 SVG 标签：<svg ... </svg>
 */
const SVG_PATTERN = /(?:```(?:svg|xml)\s*\n([\s\S]*?)```)|(<svg[\s\S]*?<\/svg>)/gi;

/**
 * 将内容拆分为 markdown 段和 svg 段的交替数组
 */
export const splitContentSegments = (content: string): readonly ContentSegment[] => {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  // 先重置正则（带 g 标志的正则有 lastIndex 状态）
  SVG_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null;

  while ((match = SVG_PATTERN.exec(content)) !== null) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;

    // match[1] = 代码块内的 SVG 内容
    // match[2] = 裸 SVG 标签
    const svgContent = match[1] ?? match[2] ?? '';

    // 只有确实是 SVG 才提取，否则保留为 markdown
    if (!isSvgContent(svgContent)) {
      continue;
    }

    // 添加匹配之前的 markdown 段
    if (matchStart > lastIndex) {
      const mdContent = content.slice(lastIndex, matchStart).trim();
      if (mdContent) {
        segments.push({type: 'markdown', content: mdContent});
      }
    }

    // 添加 SVG 段
    segments.push({type: 'svg', content: svgContent.trim()});
    lastIndex = matchEnd;
  }

  // 添加剩余的 markdown 段
  if (lastIndex < content.length) {
    const remaining = content.slice(lastIndex).trim();
    if (remaining) {
      segments.push({type: 'markdown', content: remaining});
    }
  }

  // 如果没有匹配到任何 SVG，返回整个内容作为单个 markdown 段
  if (segments.length === 0) {
    return [{type: 'markdown', content}];
  }

  return segments;
};

// ─── Block-level 元素判定辅助 ─────────────────────

const HEADING_RE = /^#{1,6}\s/;
const ORDERED_LIST_RE = /^\d+\.\s/;
const UNORDERED_LIST_RE = /^[-*+]\s/;
const HR_RE = /^---+$|^\*\*\*+$|^___+$/;

const isListLine = (s: string): boolean => ORDERED_LIST_RE.test(s) || UNORDERED_LIST_RE.test(s);
const isTableLine = (s: string): boolean => s.startsWith('|');

/**
 * 规范化 Markdown 块级结构
 *
 * LLM 返回的内容经常用单个 \n 分隔所有行，但标准 Markdown 要求：
 * - 块级元素（标题、列表、表格、代码块、引用、分割线）前必须有空行
 * - 普通文本中单个 \n 不产生换行，需要行尾双空格（硬换行）
 *
 * 本函数在不改变内容语义的情况下补齐这些格式要求：
 * 1. 代码块内部原样保留
 * 2. 块级元素前补空行（同类连续元素间不补，如表格行、列表项）
 * 3. 普通文本行尾追加双空格（Markdown 硬换行 → <br>）
 */
export const normalizeMarkdownBlocks = (text: string): string => {
  const lines = text.split('\n');
  const result: string[] = [];
  let inCodeBlock = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // ── 代码块围栏 ──
    if (trimmed.startsWith('```')) {
      if (!inCodeBlock && result.length > 0 && result[result.length - 1].trim() !== '') {
        result.push('');
      }
      inCodeBlock = !inCodeBlock;
      result.push(line);
      continue;
    }

    // 代码块内：原样保留
    if (inCodeBlock) {
      result.push(line);
      continue;
    }

    // 空行：直接保留
    if (!trimmed) {
      result.push(line);
      continue;
    }

    // ── 判断当前行类型 ──
    const isHeading = HEADING_RE.test(trimmed);
    const isList = isListLine(trimmed);
    const isTable = isTableLine(trimmed);
    const isBlockquote = trimmed.startsWith('>');
    const isHr = HR_RE.test(trimmed);
    const isBlock = isHeading || isList || isTable || isBlockquote || isHr;

    // ── 判断前一行类型（决定是否补空行）──
    const prev = result.length > 0 ? result[result.length - 1].trim() : '';

    // 块级元素前补空行，但同类连续元素间不补（保持表格/列表连续性）
    if (isBlock && prev !== ''
      && !(isTable && isTableLine(prev))
      && !(isList && isListLine(prev))
      && !(isBlockquote && prev.startsWith('>'))) {
      result.push('');
    }

    // ── 普通文本行：追加双空格硬换行 ──
    if (!isBlock && !line.endsWith('  ')) {
      result.push(line + '  ');
    } else {
      result.push(line);
    }
  }

  return result.join('\n');
};

/**
 * 预处理入口：反转义 + 规范化块级结构 + 拆分
 */
export const preprocessContent = (rawContent: string): readonly ContentSegment[] => {
  const unescaped = unescapeContent(rawContent);
  const normalized = normalizeMarkdownBlocks(unescaped);
  return splitContentSegments(normalized);
};

/**
 * 清洗 SVG 内容，移除危险元素，确保可安全渲染
 * - 移除 <script> 标签
 * - 移除内联事件处理器（onXxx）
 * - 移除 javascript: 协议
 * - 确保以 <svg 开头，以 </svg> 结尾
 */
export const sanitizeSvgForDesign = (raw: string): string | null => {
  let content = raw.trim();

  // 去除可能残留的 markdown 代码块标记
  content = content.replace(/^```(?:svg|xml|html)?\s*\n?/i, '');
  content = content.replace(/\n?```\s*$/i, '');
  content = content.trim();

  // 提取 <svg...>...</svg> 部分
  const svgMatch = content.match(/<svg[\s\S]*<\/svg>/i);
  if (!svgMatch) return null;

  let svg = svgMatch[0];

  // 安全清洗
  svg = svg.replace(/<script[\s\S]*?<\/script>/gi, '');
  svg = svg.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  svg = svg.replace(/javascript:/gi, '');

  return svg;
};

/**
 * 从消息内容中提取所有可用的 SVG 代码片段
 *
 * 支持：
 * 1. JSON 转义的 SVG
 * 2. Markdown 代码块包裹的 SVG（```svg / ```xml）
 * 3. 裸 SVG 标签
 *
 * 返回经过安全清洗的 SVG 数组
 */
export const extractAllSvgFromContent = (rawContent: string): readonly string[] => {
  const unescaped = unescapeContent(rawContent);
  const segments = splitContentSegments(unescaped);

  const svgList: string[] = [];

  for (const seg of segments) {
    if (seg.type === 'svg') {
      const cleaned = sanitizeSvgForDesign(seg.content);
      if (cleaned) {
        svgList.push(cleaned);
      }
    }
  }

  // 补充检测：markdown 段中可能还有未被 splitContentSegments 识别的 SVG 代码块
  // （例如 ```svg 代码块中内容不以 <svg 开头但包含 SVG）
  for (const seg of segments) {
    if (seg.type === 'markdown') {
      const codeBlockRegex = /```(?:svg|xml)\s*\n([\s\S]*?)```/gi;
      let match: RegExpExecArray | null;
      while ((match = codeBlockRegex.exec(seg.content)) !== null) {
        const cleaned = sanitizeSvgForDesign(match[1]);
        if (cleaned && !svgList.includes(cleaned)) {
          svgList.push(cleaned);
        }
      }
    }
  }

  return svgList;
};